import { createSelector } from 'reselect';
import { RootState } from '../../store/store';

// -------------- Примитивные селекторы для состояния каталога --------------

const catalogStateSelector = (state: RootState) => state.catalog;

const typesListSelector = createSelector(catalogStateSelector, catalog => catalog.typesList);

const marksListSelector = createSelector(catalogStateSelector, catalog => catalog.marksList);

const modelsListSelector = createSelector(catalogStateSelector, catalog => catalog.modelsList);

const modificationsListSelector = createSelector(
  catalogStateSelector,
  catalog => catalog.modificationsList,
);

const groupsListSelector = createSelector(catalogStateSelector, catalog => catalog.groupsList);

const subGroupsListSelector = createSelector(
  catalogStateSelector,
  catalog => catalog.subGroupsList,
);

const partsSelector = createSelector(catalogStateSelector, catalog => catalog.parts);

const schemeSelector = createSelector(catalogStateSelector, catalog => catalog.scheme);

const isLoadingCatalogSelector = createSelector(
  catalogStateSelector,
  catalog => catalog.isLoading.catalog,
);

const isLoadingModelSelector = createSelector(
  catalogStateSelector,
  catalog => catalog.isLoading.model,
);

const isLoadingModificationSelector = createSelector(
  catalogStateSelector,
  catalog => catalog.isLoading.modification,
);

const isLoadingGroupsSelector = createSelector(
  catalogStateSelector,
  catalog => catalog.isLoading.groups,
);

const isLoadingSubGroupsSelector = createSelector(
  catalogStateSelector,
  catalog => catalog.isLoading.subGroups,
);

const isLoadingPartsSelector = createSelector(
  catalogStateSelector,
  catalog => catalog.isLoading.parts,
);

const isLoadingSchemeSelector = createSelector(
  catalogStateSelector,
  catalog => catalog.isLoading.scheme,
);

const errorsSelector = createSelector(catalogStateSelector, catalog => catalog.errors);

const selectedCarSelector = createSelector(catalogStateSelector, catalog => catalog.selectedCar);

// -------------- Функция для загрузки из localStorage --------------
// Вспомогательная функция для загрузки состояния автомобиля из localStorage
const loadCarStateFromLocalStorage = () => {
  try {
    const carState = localStorage.getItem('carState');
    return carState ? JSON.parse(carState) : null;
  } catch (e) {
    console.error('Error reading carState from localStorage', e);
    return null;
  }
};

// -------------- Сложные селекторы для получения частей состояния --------------

// Типы автомобилей
export const selectTypesList = createSelector(typesListSelector, typesList => typesList);

// Марки автомобилей
export const selectMarksList = createSelector(marksListSelector, marksList => marksList);

// Модели автомобилей
export const selectModelsList = createSelector(modelsListSelector, modelsList => modelsList);

// Модификации автомобилей
export const selectModificationsList = createSelector(
  modificationsListSelector,
  modificationsList => modificationsList,
);

// Группы
export const selectGroupsList = createSelector(groupsListSelector, groupsList => groupsList);

// Подгруппы
export const selectSubGroupsList = createSelector(
  subGroupsListSelector,
  subGroupsList => subGroupsList,
);

// Части (детали)
export const selectParts = createSelector(partsSelector, parts => parts);

// Схема
export const selectScheme = createSelector(schemeSelector, scheme => scheme);

// Состояния загрузки
export const selectIsCatalogLoading = createSelector(
  isLoadingCatalogSelector,
  isLoading => isLoading,
);

export const selectIsModelLoading = createSelector(isLoadingModelSelector, isLoading => isLoading);

export const selectIsModificationLoading = createSelector(
  isLoadingModificationSelector,
  isLoading => isLoading,
);

export const selectIsGroupsLoading = createSelector(
  isLoadingGroupsSelector,
  isLoading => isLoading,
);

export const selectIsSubGroupsLoading = createSelector(
  isLoadingSubGroupsSelector,
  isLoading => isLoading,
);

export const selectIsPartsLoading = createSelector(isLoadingPartsSelector, isLoading => isLoading);

export const selectIsSchemeLoading = createSelector(
  isLoadingSchemeSelector,
  isLoading => isLoading,
);

// Ошибки
export const selectCatalogErrors = createSelector(errorsSelector, errors => errors.catalog);

// Выбранный тип автомобиля
export const selectSelectedCarType = createSelector(
  selectedCarSelector,
  selectedCar => loadCarStateFromLocalStorage()?.type || selectedCar.type,
);

// Выбранная марка автомобиля
export const selectSelectedCarMark = createSelector(
  selectedCarSelector,
  selectedCar => loadCarStateFromLocalStorage()?.mark || selectedCar.mark,
);

// Выбранная модель автомобиля
export const selectSelectedCarModel = createSelector(
  selectedCarSelector,
  selectedCar => loadCarStateFromLocalStorage()?.model || selectedCar.model,
);

// Выбранная модификация автомобиля
export const selectSelectedCarModification = createSelector(
  selectedCarSelector,
  selectedCar => loadCarStateFromLocalStorage()?.modification || selectedCar.modification,
);

// Выбранная группа
export const selectSelectedCarGroup = createSelector(
  selectedCarSelector,
  selectedCar => loadCarStateFromLocalStorage()?.group || selectedCar.group,
);

// Выбранная родительская группа
export const selectSelectedCarParentGroup = createSelector(
  selectedCarSelector,
  selectedCar => loadCarStateFromLocalStorage()?.parentGroup || selectedCar.parentGroup,
);

// Текущий шаг каталога
export const selectCatalogStep = createSelector(
  selectedCarSelector,
  selectedCar => loadCarStateFromLocalStorage()?.step || selectedCar.step,
);



// Предполагается, что `subGroupsList` — это массив объектов с идентификатором `id`
const selectedSubGroupIdSelector = (state: RootState) => state.catalog.selectedCar.parentGroup;

// Селектор для нахождения выбранного элемента в `subGroupsList` по идентификатору `selectedSubGroupId`
export const selectSelectedSubGroupItem = createSelector(
  subGroupsListSelector,
  selectedSubGroupIdSelector,
  (subGroupsList, selectedSubGroupId) => subGroupsList.find(subGroup => subGroup.id === selectedSubGroupId)
);